import { useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowUpDown, ChevronDown, LayoutGrid, List, SlidersHorizontal } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import type { LibraryFilter, LibraryItem, LibraryType } from '@shared/types';

import { InfiniteSentinel } from '@/components/InfiniteSentinel';
import { Masthead } from '@/components/Masthead';
import { Poster } from '@/components/Poster';
import { ResponsiveDialog } from '@/components/ResponsiveDialog';
import { Reveal, RevealGroup } from '@/components/Reveal';
import { RowSkeleton } from '@/components/RowSkeleton';
import { StateBlock } from '@/components/StateBlock';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { localized } from '@/i18n/localized';
import { useLocale, useT, type Locale } from '@/i18n/locale';
import { useCollapsingHeader } from '@/lib/useCollapsingHeader';
import { libraryItemLabel, progressPercent, STATUS_NOUN_KEY } from '@/lib/library';
import { titlePath } from '@/lib/title-path';

import { useLibrary, useWholeLibrary } from './api';
import {
  EMPTY_COPY,
  filterLibraryItems,
  readLibraryParams,
  saveViewMode,
  shelfCounts,
  SORT_LABEL_KEYS,
  sortLibraryItems,
  STATUS_FILTER_LABEL_KEYS,
  STATUS_FILTERS,
  TYPE_FILTER_LABEL_KEYS,
  TYPE_FILTERS,
  writeLibraryParam,
  type SortOrder,
  type ViewMode,
} from './filters';
import { LibraryGrid, LibraryGridSkeleton, ShelfItem, ShelfList, UserRating } from './LibraryGrid';
import { UpNextStrip } from './UpNextStrip';

const SORTS: SortOrder[] = ['recent', 'title', 'progress'];

/**
 * `/library` — the shelf (screens.md §`/library`, design.md §5.1): "Your
 * library" with a one-line summary of the shelf under it, the compact Up
 * next strip (every series in progress, a neutral one-click next episode),
 * then one quiet toolbar — status segmented with each status's count on the
 * left; type, sort and view as compact ghost controls on the right, folded
 * into one Filter sheet below `sm` (ui-ux.md §9) — over the cover wall or
 * the open row list. All four lenses live in the query string (ui-ux.md §3
 * "The URL is the state"), so reload, Back and a shared link restore the
 * exact view. Tonight (`/`) is home; this is where titles are managed.
 * Nothing on this screen is amber. Data comes from the `['home']` query
 * (api.ts) — never from a fresh mount, because PageTransition re-serves
 * cached route elements (web/docs/data-layer.md).
 *
 * Load settle (ui-ux.md §4): one `RevealGroup` — the summary rises when the
 * counts land, the strip at order 1, the first 14 covers (or 11 rows) from
 * order 3. A lens change replays the quick catalog stagger instead.
 */
export function LibraryScreen() {
  const { data, isPending, isError, refetch, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useLibrary();
  const whole = useWholeLibrary();
  const { locale, t } = useLocale();

  const [params, setParams] = useSearchParams();
  const { status, type, sort, view } = readLibraryParams(params);
  const setParam = (key: 'status' | 'type' | 'sort' | 'view', value: string) =>
    setParams((prev) => writeLibraryParam(prev, key, value), { replace: true });
  const lenses: Lenses = {
    type,
    sort,
    view,
    setType: (v) => setParam('type', v),
    setSort: (v) => setParam('sort', v),
    setView: (v) => {
      saveViewMode(v);
      setParam('view', v);
    },
  };

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { collapsed } = useCollapsingHeader(sentinelRef);

  const items = useMemo(
    () => sortLibraryItems(filterLibraryItems(data?.items ?? [], status, type), sort),
    [data, status, type, sort],
  );

  // Counts per status under the type lens — from the whole-library read
  // (every row), and only once it is complete: exact figures or none, never
  // a moving "N+" (ui-ux.md §2 Numbers).
  const counts = useMemo(
    () => (whole.data?.isComplete ? shelfCounts(whole.data.items, type) : undefined),
    [whole.data, type],
  );
  const titleCount = counts?.[status];
  const countLabel =
    titleCount === undefined
      ? undefined
      : t(titleCount === 1 ? 'library.count_singular' : 'library.count', { count: titleCount });

  // Filter/sort change re-keys the collection so its mount stagger replays.
  const collectionKey = `${status}:${type}:${sort}`;
  // Only the view's first collection settles; once a lens has changed it is
  // an interaction, and the collection takes the quick catalog stagger.
  const [firstLens] = useState(`${collectionKey}:${view}`);
  const [lensed, setLensed] = useState(false);
  if (!lensed && `${collectionKey}:${view}` !== firstLens) setLensed(true);

  return (
    <RevealGroup id="library">
      <section className="flex flex-col gap-8">
        {/* The summary is quiet meta under the title; a non-breaking space
            holds its line until the exact counts are known, so nothing shifts. */}
        <Masthead
          className="contents"
          title={t('library.heading')}
          meta={counts ? <ShelfSummary counts={counts} /> : ' '}
          collapsed={collapsed}
        />
        {/* Sentinel marks the masthead's bottom edge for useCollapsingHeader; the
            -my-8 cancels the section gaps on both sides so it adds no space. */}
        <div ref={sentinelRef} aria-hidden className="-my-8 h-px w-full" />

        <RevealGroup id="library-upnext" order={1}>
          <UpNextStrip />
        </RevealGroup>

        <div className="flex items-center gap-3 sm:flex-wrap">
          {/* Below `sm` the status row scrolls on its own, snapping to a chip. */}
          <div className="scrollbar-none -ml-4 min-w-0 flex-1 snap-x snap-mandatory scroll-pl-4 overflow-x-auto pl-4 sm:ml-0 sm:flex-none sm:overflow-x-visible sm:pl-0">
            <ToggleGroup
              type="single"
              aria-label={t('library.filterByStatus')}
              value={status}
              onValueChange={(v) => {
                if (v) setParam('status', v as LibraryFilter);
              }}
              className="w-max"
            >
              {STATUS_FILTERS.map((f) => (
                <ToggleGroupItem key={f} value={f} size="sm" className="snap-start">
                  {t(STATUS_FILTER_LABEL_KEYS[f])}
                  {counts?.[f] ? (
                    <>
                      {' '}
                      <span className="tabular-nums text-ink-tertiary">{counts[f]}</span>
                    </>
                  ) : null}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
          <InlineLenses {...lenses} />
          <FilterSheet {...lenses} />
        </div>

        <RevealGroup id="library-shelf" order={3}>
          <div>
            {countLabel ? (
              <p className="sr-only" role="status">
                {countLabel}
              </p>
            ) : null}
            {isPending ? (
              view === 'grid' ? (
                <LibraryGridSkeleton label={t('library.loading')} />
              ) : (
                <RowSkeleton count={6} variant="poster" label={t('library.loading')} />
              )
            ) : isError ? (
              <ErrorState onRetry={() => void refetch()} />
            ) : items.length === 0 ? (
              <EmptyState filter={status} />
            ) : (
              <>
                {view === 'grid' ? (
                  <LibraryGrid
                    key={collectionKey}
                    items={items}
                    locale={locale}
                    filter={status}
                    settle={!lensed}
                  />
                ) : (
                  <LibraryRows key={collectionKey} items={items} locale={locale} settle={!lensed} />
                )}
                {/* More of the library may exist past the loaded pages (GET
                    /v1/home pages at 20 rows) — keep loading regardless of the
                    active filter so a filtered view can keep revealing matches
                    as more of the raw library streams in. */}
                <InfiniteSentinel
                  hasMore={Boolean(hasNextPage)}
                  loading={isFetchingNextPage}
                  onReach={() => void fetchNextPage()}
                />
              </>
            )}
          </div>
        </RevealGroup>
      </section>
    </RevealGroup>
  );
}

/** Summary parts, in reading order; a zero part is left out. */
const SUMMARY_PARTS = [
  ['watching', 'library.summary.watching'],
  ['completed', 'library.summary.completed'],
  ['watchlist', 'library.summary.watchlist'],
] as const;

/**
 * The masthead's one calm line: "100 titles · 4 watching · 76 completed ·
 * 12 to watch", under the type lens (Dropped lives on its chip). The dots
 * are decoration, hidden from assistive tech.
 */
function ShelfSummary({ counts }: { counts: Record<LibraryFilter, number> }) {
  const t = useT();
  const parts = [
    t(counts.all === 1 ? 'library.count_singular' : 'library.count', { count: counts.all }),
    ...SUMMARY_PARTS.filter(([status]) => counts[status] > 0).map(([status, key]) =>
      t(status === 'completed' && counts[status] === 1 ? `${key}_singular` : key, {
        count: counts[status],
      }),
    ),
  ];
  return (
    <Reveal className="tabular-nums">
      {parts.map((part, i) => (
        <span key={part}>
          {i > 0 ? <span aria-hidden> · </span> : null}
          <span>{part}</span>
        </span>
      ))}
    </Reveal>
  );
}

interface Lenses {
  type: LibraryType;
  sort: SortOrder;
  view: ViewMode;
  setType: (v: LibraryType) => void;
  setSort: (v: SortOrder) => void;
  setView: (v: ViewMode) => void;
}

/** `sm`+: type and sort as compact ghost menus, then the grid/list toggle. */
function InlineLenses({ type, sort, view, setType, setSort, setView }: Lenses) {
  const t = useT();
  return (
    <div className="ml-auto hidden items-center gap-1 sm:flex">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('library.typeA11y', { type: t(TYPE_FILTER_LABEL_KEYS[type]) })}
          >
            {t(TYPE_FILTER_LABEL_KEYS[type])}
            <ChevronDown aria-hidden className="size-3.5 text-ink-tertiary" strokeWidth={1.75} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('library.sheet.type')}</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={type} onValueChange={(v) => setType(v as LibraryType)}>
            {TYPE_FILTERS.map((f) => (
              <DropdownMenuRadioItem key={f} value={f}>
                {t(TYPE_FILTER_LABEL_KEYS[f])}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`${t('library.sortBy')} ${t(SORT_LABEL_KEYS[sort])}`}
          >
            <ArrowUpDown aria-hidden className="size-4" strokeWidth={1.75} />
            {t(SORT_LABEL_KEYS[sort])}
            <ChevronDown aria-hidden className="size-3.5 text-ink-tertiary" strokeWidth={1.75} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('library.sortBy')}</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={sort} onValueChange={(v) => setSort(v as SortOrder)}>
            {SORTS.map((s) => (
              <DropdownMenuRadioItem key={s} value={s}>
                {t(SORT_LABEL_KEYS[s])}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <ToggleGroup
        type="single"
        aria-label={t('library.collectionView')}
        value={view}
        onValueChange={(v) => {
          if (v) setView(v as ViewMode);
        }}
      >
        <ToggleGroupItem value="grid" size="sm" aria-label={t('library.viewAsGrid')}>
          <LayoutGrid aria-hidden className="size-4" strokeWidth={1.75} />
        </ToggleGroupItem>
        <ToggleGroupItem value="list" size="sm" aria-label={t('library.viewAsList')}>
          <List aria-hidden className="size-4" strokeWidth={1.75} />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}

/**
 * Below `sm`: one Filter button opening a sheet with type, sort and view
 * (ui-ux.md §9 "First content above the fold"), so the status row stays
 * inline and the first cover stays high. Choices apply at once (URL
 * `replace`); Done just closes.
 */
function FilterSheet({ type, sort, view, setType, setSort, setView }: Lenses) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const id = useId();
  const group = (key: string, label: string, control: ReactNode) => (
    <div className="flex flex-col gap-2">
      <p id={`${id}-${key}`} className="text-subhead text-ink-secondary">
        {label}
      </p>
      {control}
    </div>
  );
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="shrink-0 sm:hidden"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <SlidersHorizontal aria-hidden className="size-4" strokeWidth={1.75} />
        {t('library.sheet.open')}
      </Button>
      <ResponsiveDialog
        open={open}
        onOpenChange={setOpen}
        title={t('library.sheet.title')}
        footer={
          <Button variant="neutral" onClick={() => setOpen(false)}>
            {t('common.done')}
          </Button>
        }
      >
        <div className="flex flex-col gap-6 pb-2">
          {group(
            'type',
            t('library.sheet.type'),
            <ToggleGroup
              type="single"
              aria-labelledby={`${id}-type`}
              value={type}
              onValueChange={(v) => {
                if (v) setType(v as LibraryType);
              }}
              className="w-max"
            >
              {TYPE_FILTERS.map((f) => (
                <ToggleGroupItem key={f} value={f} size="sm">
                  {t(TYPE_FILTER_LABEL_KEYS[f])}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>,
          )}
          {group(
            'sort',
            t('library.sortBy'),
            <ToggleGroup
              type="single"
              aria-labelledby={`${id}-sort`}
              value={sort}
              onValueChange={(v) => {
                if (v) setSort(v as SortOrder);
              }}
              className="w-max"
            >
              {SORTS.map((s) => (
                <ToggleGroupItem key={s} value={s} size="sm">
                  {t(SORT_LABEL_KEYS[s])}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>,
          )}
          {group(
            'view',
            t('library.sheet.view'),
            <ToggleGroup
              type="single"
              aria-labelledby={`${id}-view`}
              value={view}
              onValueChange={(v) => {
                if (v) setView(v as ViewMode);
              }}
              className="w-max"
            >
              <ToggleGroupItem value="grid" size="sm">
                <LayoutGrid aria-hidden className="size-4" strokeWidth={1.75} />
                {t('library.view.grid')}
              </ToggleGroupItem>
              <ToggleGroupItem value="list" size="sm">
                <List aria-hidden className="size-4" strokeWidth={1.75} />
                {t('library.view.list')}
              </ToggleGroupItem>
            </ToggleGroup>,
          )}
        </div>
      </ResponsiveDialog>
    </>
  );
}

/**
 * The open row list (ui-ux.md §3 Rows): a 44px cover thumb, title, status,
 * your rating, progress — a table-like rhythm from `sm` with no boxes and no
 * rules; rows are separated by space and a hover wash. Below `sm` the cells
 * wrap under the title as spaced spans. On the first load the rows rise as
 * settle lines (the first 11; `SETTLE.maxOrder`), the rest at rest.
 */
function LibraryRows({ items, locale, settle }: { items: LibraryItem[]; locale: Locale; settle: boolean }) {
  const t = useT();
  return (
    <ShelfList count={items.length} className="m-0 flex list-none flex-col gap-1 p-0">
      {items.map((item, i) => {
        const name = localized(item.title.title, item.title.titleBr, locale);
        const pct = Math.min(100, Math.round(progressPercent(item)));
        const showProgress = item.status === 'watching' && pct > 0;
        return (
          <ShelfItem key={item.title.id} settle={settle} order={i}>
            <Link
              to={titlePath(item.title.id, item.title.type)}
              aria-label={libraryItemLabel(item, locale)}
              className="flex items-center gap-4 rounded px-3 py-2 transition-colors hover:bg-surface-alt"
            >
              <div className="w-11 shrink-0">
                <Poster
                  id={item.title.id}
                  name={name}
                  palette={item.title.palette}
                  imageUrl={item.title.posterUrl}
                  imageFallbackUrl={item.title.posterFallbackUrl}
                  sizes="44px"
                  fallbackTitle={false}
                />
              </div>
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-0.5 sm:grid sm:grid-cols-[minmax(0,1fr)_7rem_3rem_10rem] sm:gap-x-6">
                <p className="min-w-0 basis-full truncate text-subhead text-ink sm:basis-auto">{name}</p>
                <span className="text-caption text-ink-secondary">
                  {item.status === 'removed' ? null : t(STATUS_NOUN_KEY[item.status])}
                </span>
                <span className="empty:hidden sm:empty:block">
                  {item.userRating ? <UserRating value={item.userRating} /> : null}
                </span>
                <span className="flex items-center gap-2 empty:hidden sm:empty:flex">
                  {showProgress ? (
                    <>
                      <span aria-hidden className="h-1 w-16 overflow-hidden rounded-full bg-surface-alt">
                        <span className="block h-full rounded-full bg-ink" style={{ width: `${pct}%` }} />
                      </span>
                      <span className="text-caption tabular-nums text-ink-secondary">
                        {item.totalEpisodes > 0
                          ? t('upnext.progressOf', { watched: item.watchedCount, total: item.totalEpisodes })
                          : `${pct}%`}
                      </span>
                    </>
                  ) : null}
                </span>
              </div>
            </Link>
          </ShelfItem>
        );
      })}
    </ShelfList>
  );
}

function EmptyState({ filter }: { filter: LibraryFilter }) {
  const copy = EMPTY_COPY[filter];
  const t = useT();
  return (
    <StateBlock
      role="status"
      overline={t(copy.overlineKey)}
      title={t(copy.bodyKey)}
      action={
        <Button asChild variant="neutral">
          <Link to="/discover">{t('common.findSomething')}</Link>
        </Button>
      }
    />
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <StateBlock
      role="alert"
      overline={t('library.overline')}
      title={t('library.error.load')}
      action={
        <Button variant="neutral" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      }
    />
  );
}
